import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Api } from '@.api';
import { Layout } from '@.layout';
import { Table2 } from '@.partials';
import { Auth, LinkHelper } from '@.services';
import { PermissionProps, Request } from '@.types';
import { Button, Filter3 } from '@.ui';
import { ManagementFilesResponseType } from '@/Services/api/methods';
import { useModalTableConfirm } from '@/Shared/lib/useModalTableConfirm';

export * from './FilesAction';

export const Files: React.FC<PermissionProps> = ({ permissions }) => {
    const auth = Auth.Use();
    const navigator = useNavigate();

    const get = useCallback((q?: Request.All) => Api.managementFiles().getAll(q), []);

    const modalTableArchiveConfirm = useModalTableConfirm({
        message: 'Do you really want to archive this file?',
        confirm: (id: number) => Api.managementFiles().archive(id),
    });

    const modalTableRestoreConfirm = useModalTableConfirm({
        message: 'Do you really want to restore this file?',
        confirm: (id: number) => Api.managementFiles().restore(id),
    });

    const modalTableDestroyConfirm = useModalTableConfirm({
        message: 'Do you really want to permanently delete this file?',
        confirm: (id: number) => Api.managementFiles().destroy(id),
    });

    const getData = useCallback((d: ManagementFilesResponseType) => d.management_files, []);
    const item = useCallback((item: ManagementFilesResponseType['management_files'][number]) => ({ className: item.deleted_at ? 'deleted' : '' }), []);

    const modals = [modalTableArchiveConfirm.modal, modalTableRestoreConfirm.modal, modalTableDestroyConfirm.modal];

    return (
        <Layout name="data" breadcrumbs={[{ name: 'Files management', current: true }]}>
            {modals}

            <h1>
                Files management
                <Button
                    color="brown"
                    text="Add update"
                    onClick={() => navigator(LinkHelper.dataFilesAdd)}
                    disabled={!permissions?.create && !auth?.user?.is_super}
                />
            </h1>
            <Table2 get={get} getData={getData} item={item} export="management_files">
                {{
                    filters: (handler) => (
                        <Filter3 onChange={handler}>
                            <Filter3.Search />
                            <Filter3.ManagementFileTypes />
                            {auth?.user?.is_super && <Filter3.Boolean placeholder="Archived" name="is_archived" />}
                        </Filter3>
                    ),
                    items: [
                        (item) => ({
                            name: 'Name',
                            max: 20,
                            content: item.name,
                            sort: 'name',
                            link: LinkHelper.dataFilesEditId(item.id),
                            disabled: !!item.deleted_at || !permissions?.update || !auth?.user?.is_super,
                        }),
                        (item) => ({
                            name: 'Version',
                            max: 10,
                            content: item.version,
                            sort: 'version',
                        }),
                        (item) => ({
                            name: 'Type',
                            max: 20,
                            content: item.type.name,
                            sort: 'type',
                        }),
                        (item) => ({
                            name: 'Description',
                            max: 20,
                            content: item.description,
                        }),
                    ],
                    actions: [
                        ({ deleted_at, file }) => ({
                            type: 'download',
                            disabled: !!deleted_at,
                            onClick: () => {
                                const w = window.open(file.url, '_blank', 'noopener,noreferrer');
                                if (w) w.opener = null;
                            },
                        }),
                        ({ id, deleted_at }) => ({
                            type: 'edit',
                            link: LinkHelper.dataFilesEditId(id),
                            disabled: !!deleted_at || (!permissions?.update && !auth?.user?.is_super),
                        }),
                        ({ id, deleted_at }) => ({
                            type: deleted_at ? 'restore' : 'archive',
                            onClick: deleted_at ? () => modalTableRestoreConfirm.action(id) : () => modalTableArchiveConfirm.action(id),
                            disabled: !permissions?.delete && !auth?.user?.is_super,
                        }),
                        ({ id }) => ({
                            type: 'destroy',
                            onClick: () => modalTableDestroyConfirm.action(id),
                            hide: !permissions?.admin,
                        }),
                    ],
                }}
            </Table2>
        </Layout>
    );
};
